import React, {
  Dispatch, FormEvent, SetStateAction, useState,
} from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, FormControlLabel, Radio, RadioGroup, Stack, Typography,
} from '@mui/material';
import styled from '@emotion/styled/macro';
import Divider from '@mui/material/Divider';
import { MdOutlineClose } from 'react-icons/md';

const ModalHeader = styled.div`
  font-size: 20px;
  padding: 12px;
`;

const ModalBody = styled.div`
  display: flex;
  gap: 9px;
  flex-direction: column;
`;

const ModalDivider = styled(Divider)`
`;

const ModalHeaderActionContainer = styled(Stack)`
  justify-content: space-between;
`;

const CloseButton = styled(Button)`
  font-size: 20px;
  color: black;
`;

const StyledUl = styled('ul')`
  margin-left: 20px;
  margin-top: -5px;

  li {
    font-size: 14px;
  }
`;

const SubmitButton = styled(Button)`
  width: 120px;
`;

const ErrorTypography = styled(Typography)`
  color: red;
`;

type AddAccountUserModalStepTwoProps = {
    handleClose: () => void;
    goToNextStep: (method: string) => void;
    goToPreviousStep: () => void;
    method: string;
    firstName: string;
    lastName: string;
};

type ViewProps = Omit<AddAccountUserModalStepTwoProps, 'goToNextStep' | 'method'> & {
    handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
    accessMethodInfo: AccessMethodInfo;
    setAccessMethodInfo: Dispatch<SetStateAction<AccessMethodInfo>>;
};

function View({
  handleClose, handleSubmit, goToPreviousStep, firstName, lastName, accessMethodInfo, setAccessMethodInfo,
}: ViewProps): React.JSX.Element {
  return (
    <Dialog
      open
      onClose={handleClose}
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <ModalHeaderActionContainer spacing={2} direction="row">
          <ModalHeader>
            How will
            {' '}
            <em>
              {firstName}
              {' '}
              {lastName}
            </em>
            {' '}
            be granted aircraft access?
          </ModalHeader>
          <CloseButton variant="text" onClick={handleClose}>
            <MdOutlineClose />
          </CloseButton>
        </ModalHeaderActionContainer>
        <ModalDivider />
        <DialogContent>
          <ModalBody>
            <RadioGroup
              name="accessMethod"
              defaultValue={accessMethodInfo.method}
              onChange={(x) => {
                setAccessMethodInfo({
                  method: x.target.value,
                  validationMessage: '',
                });
              }}
            >
              <FormControlLabel value="Fleet" control={<Radio />} label={<Typography fontSize="14px" fontWeight="bold">Fleet of Aircraft</Typography>} />
              <StyledUl>
                <li>User will have access to one or more fleet of aircraft</li>
                <li>User will receive or lose permissions to aircraft automatically as a fleet changes</li>
              </StyledUl>

              <br />

              <FormControlLabel value="Aircraft" control={<Radio />} label={<Typography fontSize="14px" fontWeight="bold">Specific Aircraft</Typography>} />
              <StyledUl>
                <li>User will have access to specific aircraft which will only change if you explicitly update their permissions later</li>
              </StyledUl>
            </RadioGroup>
            {!!accessMethodInfo.validationMessage && (
            <ErrorTypography>
              *
              {' '}
              {accessMethodInfo.validationMessage}
            </ErrorTypography>
            )}
          </ModalBody>
        </DialogContent>
        <DialogActions>
          <Stack spacing={2} direction="row" justifyContent="end" width="100%">
            <Button onClick={goToPreviousStep}>Back</Button>
            <SubmitButton variant="contained" color="primary" type="submit">
              Next
            </SubmitButton>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
}

type AccessMethodInfo = { method: string; validationMessage: string; };
function Model({
  handleClose, goToNextStep, goToPreviousStep, firstName, lastName, method,
}: AddAccountUserModalStepTwoProps): React.JSX.Element {
  const [accessMethodInfo, setAccessMethodInfo] = useState<AccessMethodInfo>({
    method,
    validationMessage: '',
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    if (!accessMethodInfo.method) {
      setAccessMethodInfo((p) => ({
        ...p,
        validationMessage: 'Required',
      }));

      return;
    }

    goToNextStep(accessMethodInfo.method);
  };

  return (
    <View
      handleClose={handleClose}
      handleSubmit={handleSubmit}
      goToPreviousStep={goToPreviousStep}
      firstName={firstName}
      lastName={lastName}
      accessMethodInfo={accessMethodInfo}
      setAccessMethodInfo={setAccessMethodInfo}
    />
  );
}

export default Model;
